import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mcGetChains } from '../lib/api';
import TokenPoolSimulator from '../components/TokenPoolSimulator';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeftRight, Search } from 'lucide-react';
import { toast } from 'sonner';

const EVM_ADDR = /^0x[0-9a-fA-F]{40}$/;
const SOL_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const KROWN_ADDR = '0x9189d145d1d8e612c4c48bbd1af5a50cbee63d7d';

export default function SwapSimulatorPage() {
  const [sp, setSp] = useSearchParams();
  const chain = sp.get('chain');
  const token = sp.get('token');

  const [chains, setChains] = useState(null);
  const [formChain, setFormChain] = useState('ethereum');
  const [formToken, setFormToken] = useState('');

  useEffect(() => { mcGetChains().then((d) => setChains(d.chains || [])).catch(() => {}); }, []);

  // Krown needs no token input — it resolves to its own indexed token.
  const isKrownForm = formChain === 'krown';

  const submitForm = (e) => {
    e.preventDefault();
    if (isKrownForm) { setSp({ chain: 'krown', token: KROWN_ADDR }); return; }
    const t = formToken.trim();
    const isEvm = EVM_ADDR.test(t);
    const isSol = SOL_RE.test(t) && !isEvm;
    if (!isEvm && !isSol) { toast.error('Enter a valid token contract address'); return; }
    const c = isSol ? 'solana' : formChain;
    setSp({ chain: c, token: t });
  };

  const ready = chain && token;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="swap-simulator-page">
      <header>
        <div className="flex items-center gap-2 text-foreground mb-2">
          <ArrowLeftRight className="w-5 h-5" />
          <span className="mono-label">Swap &amp; Liquidity Simulator</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-none">
          Multichain AMM Price-Impact Simulator
        </h1>
        <p className="text-muted-foreground mt-2 text-sm max-w-2xl leading-relaxed">
          Pick any token on any chain, browse its live pools, then model how a buy, sell or
          liquidity change moves price and market cap — reserves are read live, nothing is hardcoded.
        </p>
      </header>

      {ready ? (
        <>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setSp({})} data-testid="sim-change-token">
              Change token
            </Button>
          </div>
          <TokenPoolSimulator key={`${chain}:${token}`} chain={chain} token={token} />
        </>
      ) : (
        <Card data-testid="sim-empty-state">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-4">
              Open any token from the Explorer and hit <span className="font-medium text-foreground">Swap Simulator</span>,
              pick a chain below, or paste a token contract.
            </p>
            <form onSubmit={submitForm} className="flex flex-col sm:flex-row gap-2">
              <select
                value={formChain}
                onChange={(e) => setFormChain(e.target.value)}
                className="h-11 rounded border border-input bg-card px-3 text-sm font-medium"
                data-testid="sim-form-chain"
              >
                {(chains || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              {!isKrownForm && (
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={formToken}
                    onChange={(e) => setFormToken(e.target.value)}
                    placeholder="Token contract address"
                    className="pl-10 font-mono h-11"
                    data-testid="sim-form-token"
                  />
                </div>
              )}
              {isKrownForm && (
                <div className="flex-1 flex items-center text-sm text-muted-foreground px-3 border border-dashed border-border rounded h-11">
                  Krown resolves to its own indexed token automatically.
                </div>
              )}
              <Button type="submit" className="h-11 px-6" data-testid="sim-form-submit">Load</Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
